import { getRenewalStatus } from '../../providers/RenewalAPI';
import { RenewalStatus } from '../models/ResourceModels';
import { isRenewalStatusTerminal, reduceRenewalEvents } from './RenewalStatusService';
import {
  DETAIL_FAST_INTERVAL_MS,
  DETAIL_FAST_PHASE_DURATION_MS,
  DETAIL_SLOW_INTERVAL_MS,
  MAX_POLLING_DURATION_MS,
  PRESENCE_INTERVAL_MS,
  REQUEST_TIMEOUT_MS
} from '../../constants/RenewalPolling';

export type PollerMode = 'presence' | 'detail';
export type PollerListener = (status: RenewalStatus | undefined, isPollingStopped: boolean) => void;

interface PollerEntry {
  presenceListeners: Set<PollerListener>;
  detailListeners: Set<PollerListener>;
  activeMode: PollerMode | null;
  status: RenewalStatus | undefined;
  isPollingStopped: boolean;
  timeoutId: ReturnType<typeof setTimeout> | undefined;
  inFlight: boolean;
  // Incremented every time a request starts for this entry. A request's completion
  // handler captures the token it started with and only acts if the entry it reads back
  // is still the same object *and* still on the same token — guards against a stale
  // request (from a torn-down-and-recreated entry, or one superseded by a newer request
  // on the same entry) mutating state that no longer belongs to it.
  requestToken: number;
  abortController: AbortController | undefined;
  // Set when an immediate refetch was requested (forceRefresh / a mode switch) while a
  // request was already in flight, so it isn't silently dropped — it fires as soon as
  // the in-flight request settles instead of waiting for it to schedule the next tick.
  pendingImmediateRefetch: boolean;
  // Set by notifyOptimisticTrigger to the recordedAt of whatever was latest *before* the
  // trigger (or null if there was no prior history). The immediate reconciliation fetch
  // that follows a trigger can race the backend — if the response's latest event still
  // matches this baseline, the new attempt hasn't been recorded yet, so that response
  // must not overwrite the optimistic "Triggered" status or be treated as terminal.
  // undefined means "not currently reconciling an optimistic trigger".
  reconciliationBaseline: string | null | undefined;
  // Reset whenever activeMode changes, so the MAX_POLLING_DURATION_MS cap applies per
  // polling-session-per-mode rather than accumulating across a presence->detail switch.
  modeStartedAt: number;
  paused: boolean;
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

// Module-level singleton, one entry per accountId — mirrors the plain-module idiom
// already used by RenewalAPI.ts (no React Context exists anywhere in this codebase).
// Ensures at most one in-flight request and one scheduled timer per accountId, and that
// a 'detail' (modal-open) subscriber always supersedes a 'presence' (row-expanded) one
// rather than both polling independently.
const entries = new Map<string, PollerEntry>();

function createEntry(): PollerEntry {
  return {
    presenceListeners: new Set(),
    detailListeners: new Set(),
    activeMode: null,
    status: undefined,
    isPollingStopped: false,
    timeoutId: undefined,
    inFlight: false,
    requestToken: 0,
    abortController: undefined,
    pendingImmediateRefetch: false,
    reconciliationBaseline: undefined,
    modeStartedAt: Date.now(),
    // A subscription created while the tab is already hidden must not start polling —
    // it stays paused until the visibilitychange listener below sees the tab regain focus.
    paused: isDocumentHidden()
  };
}

function computeActiveMode(entry: PollerEntry): PollerMode | null {
  if (entry.detailListeners.size > 0) {
    return 'detail';
  }
  if (entry.presenceListeners.size > 0) {
    return 'presence';
  }
  return null;
}

function notify(entry: PollerEntry): void {
  entry.presenceListeners.forEach(listener => listener(entry.status, entry.isPollingStopped));
  entry.detailListeners.forEach(listener => listener(entry.status, entry.isPollingStopped));
}

function clearScheduled(entry: PollerEntry): void {
  if (entry.timeoutId !== undefined) {
    clearTimeout(entry.timeoutId);
    entry.timeoutId = undefined;
  }
}

function cadenceFor(entry: PollerEntry): number {
  if (entry.activeMode === 'detail') {
    const elapsed = Date.now() - entry.modeStartedAt;
    return elapsed < DETAIL_FAST_PHASE_DURATION_MS ? DETAIL_FAST_INTERVAL_MS : DETAIL_SLOW_INTERVAL_MS;
  }
  return PRESENCE_INTERVAL_MS;
}

function scheduleNext(accountId: string, entry: PollerEntry): void {
  if (entry.paused || !entry.activeMode) {
    return;
  }
  if (Date.now() - entry.modeStartedAt >= MAX_POLLING_DURATION_MS) {
    entry.isPollingStopped = true;
    notify(entry);
    return;
  }
  clearScheduled(entry);
  entry.timeoutId = setTimeout(() => runFetch(accountId), cadenceFor(entry));
}

// Fetches immediately if the entry is idle; if a request is already in flight, queues
// the request rather than dropping it — it fires the moment the in-flight one settles.
function requestImmediateFetch(accountId: string, entry: PollerEntry): void {
  if (entry.inFlight) {
    entry.pendingImmediateRefetch = true;
    return;
  }
  clearScheduled(entry);
  runFetch(accountId);
}

// After a request settles: either honor a queued immediate-refetch request, or fall
// back to the normal cadence. Returns true if a new request was started.
function consumePendingImmediateRefetch(accountId: string, entry: PollerEntry): boolean {
  if (!entry.pendingImmediateRefetch) {
    return false;
  }
  entry.pendingImmediateRefetch = false;
  clearScheduled(entry);
  runFetch(accountId);
  return true;
}

function runFetch(accountId: string): void {
  const entry = entries.get(accountId);
  if (!entry?.activeMode || entry?.inFlight || entry?.paused) {
    return;
  }
  entry.inFlight = true;
  const token = ++entry.requestToken;
  const controller = new AbortController();
  entry.abortController = controller;
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  getRenewalStatus(accountId, controller.signal).then(response => {
    clearTimeout(timeoutId);
    const current = entries.get(accountId);
    // Ignore a completion that no longer belongs to the entry/request that started it —
    // e.g. the entry was torn down (last subscriber left) and a new one created for the
    // same account before this settled, or a newer request has since started on it.
    if (!current || current !== entry || current.requestToken !== token) {
      return;
    }
    current.inFlight = false;
    current.abortController = undefined;

    if (response.httpstatus === 401) {
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    if (response.httpstatus === 200) {
      const { latest, state } = reduceRenewalEvents(response.events);

      if (current.reconciliationBaseline !== undefined) {
        // Waiting to confirm the backend has recorded the attempt we just optimistically
        // triggered. If the latest event still matches what was there *before* the
        // trigger, this response is stale (racing the backend) — it must not overwrite
        // the optimistic "Triggered" status, and must never be treated as terminal.
        if ((latest?.recordedAt ?? null) === current.reconciliationBaseline) {
          if (!consumePendingImmediateRefetch(accountId, current)) {
            scheduleNext(accountId, current);
          }
          return;
        }
        current.reconciliationBaseline = undefined;
      }

      current.status = response;
      current.isPollingStopped = false;
      notify(current);
      // Retry gating is based only on terminal state, never on `present` — an empty/
      // present:false response is valid data (no history yet), not a signal to give up.
      if (isRenewalStatusTerminal(state)) {
        current.pendingImmediateRefetch = false;
      } else if (!consumePendingImmediateRefetch(accountId, current)) {
        scheduleNext(accountId, current);
      }
    } else {
      // Soft-fail: keep the last-known-good status displayed, retry on the same cadence.
      notify(current);
      if (!consumePendingImmediateRefetch(accountId, current)) {
        scheduleNext(accountId, current);
      }
    }
  }).catch(() => {
    clearTimeout(timeoutId);
    const current = entries.get(accountId);
    if (!current || current !== entry || current.requestToken !== token) {
      return;
    }
    current.inFlight = false;
    current.abortController = undefined;
    // Network-level failure, or the request timing out via the abort above — treat the
    // same as a soft-fail.
    if (!consumePendingImmediateRefetch(accountId, current)) {
      scheduleNext(accountId, current);
    }
  });
}

function handleModeTransition(accountId: string, entry: PollerEntry, nextMode: PollerMode | null, forceImmediateFetch: boolean): void {
  if (nextMode === entry.activeMode) {
    return;
  }
  entry.activeMode = nextMode;
  entry.modeStartedAt = Date.now();
  entry.isPollingStopped = false;
  clearScheduled(entry);
  if (nextMode && forceImmediateFetch) {
    requestImmediateFetch(accountId, entry);
  } else if (nextMode) {
    scheduleNext(accountId, entry);
  } else {
    // Last subscriber gone — cancel any in-flight request outright rather than letting
    // it complete uselessly (its result will be ignored anyway via the token/identity
    // guard in runFetch, but aborting frees the connection immediately).
    entry.abortController?.abort();
    entries.delete(accountId);
  }
}

export function subscribe(accountId: string, mode: PollerMode, listener: PollerListener): () => void {
  let entry = entries.get(accountId);
  if (!entry) {
    entry = createEntry();
    entries.set(accountId, entry);
  }
  const previousMode = entry.activeMode;
  const set = mode === 'detail' ? entry.detailListeners : entry.presenceListeners;
  set.add(listener);

  const nextMode = computeActiveMode(entry);
  if (nextMode !== previousMode) {
    // Switching mode (including the first subscriber overall) always refetches
    // immediately, so e.g. opening the modal doesn't wait out a stale presence tick.
    handleModeTransition(accountId, entry, nextMode, true);
  } else {
    // Same mode already active (e.g. a second presence subscriber) — replay latest.
    listener(entry.status, entry.isPollingStopped);
  }

  return () => {
    set.delete(listener);
    const current = entries.get(accountId);
    if (!current) {
      return;
    }
    const remainingMode = computeActiveMode(current);
    handleModeTransition(accountId, current, remainingMode, false);
  };
}

// Called after a successful trigger POST so the pill reflects "in progress" immediately
// rather than waiting for the next poll tick to confirm it.
export function notifyOptimisticTrigger(accountId: string): void {
  const entry = entries.get(accountId);
  if (!entry) {
    return; // nothing currently subscribed for this account (e.g. row not expanded)
  }
  // Record what was latest *before* this trigger, so the reconciliation fetch below can
  // tell a genuinely-new attempt apart from a response that still reflects the old one.
  const { latest: previousLatest } = reduceRenewalEvents(entry.status?.events);
  entry.reconciliationBaseline = previousLatest?.recordedAt ?? null;
  entry.status = {
    present: true,
    events: [{ state: 'TRIGGERED', recordedAt: new Date().toISOString() }]
  };
  entry.isPollingStopped = false;
  notify(entry);
  forceRefresh(accountId);
}

// Used by the manual "Refresh status" affordance shown once polling has given up.
export function forceRefresh(accountId: string): void {
  const entry = entries.get(accountId);
  if (!entry?.activeMode) {
    return;
  }
  entry.modeStartedAt = Date.now();
  entry.isPollingStopped = false;
  requestImmediateFetch(accountId, entry);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      entries.forEach(entry => {
        entry.paused = true;
        clearScheduled(entry);
      });
    } else {
      entries.forEach((entry, accountId) => {
        if (!entry.activeMode) {
          return;
        }
        entry.paused = false;
        requestImmediateFetch(accountId, entry);
      });
    }
  });
}
